import { tooltipAnatomy } from "@chakra-ui/react-v3/anatomy";
import { defineSlotRecipe } from "@chakra-ui/react-v3";

export const tooltipRecipe = defineSlotRecipe({
  slots: tooltipAnatomy.keys(),

  base: {
    content: {
      zIndex: 1800,
      "--tooltip-bg": "colors.colorPalette.subtle",
      "--tooltip-border-color": "colors.colorPalette.emphasized",
      position: "relative",
      border: "1px solid",
      borderColor: "var(--tooltip-border-color)",
      borderRadius: "lg",
      bg: "var(--tooltip-bg)",
      color: "fg.default",
      px: "2.5",
      py: "1",
      textStyle: "body.12",
      fontWeight: "normal",
      maxW: "xs",
      transformOrigin: "var(--transform-origin)",
      display: "flex",
      flexDirection: "column",
      _open: {
        animationName: "scale-in, fade-in",
        animationDuration: "fast",
      },
      _closed: {
        animationName: "scale-out, fade-out",
        animationDuration: "fast",
      },
    },

    arrow: {
      "--arrow-size": "sizes.2",
      "--arrow-background": "var(--tooltip-bg)",
    },

    arrowTip: {
      bg: "var(--tooltip-bg)",
      borderTopWidth: "1px",
      borderLeftWidth: "1px",
      borderColor: "var(--tooltip-border-color)",
    },
  },

  variants: {
    size: {
      sm: {
        content: {
          px: "3",
          py: "2",
          gap: "1",
        },
      },
      md: {
        content: {
          px: "4",
          py: "3",
          gap: "2",
        },
      },
    },

    variant: {
      subtle: {},
      // Gradient border on the container surface, matching the Tiger CLI nudge
      // in the top nav. Two stacked backgrounds keep the frame a single element.
      gradient: {
        content: {
          "--tooltip-bg": "colors.bg.container",
          "--tooltip-gradient-from": "colors.highlight.fg",
          "--tooltip-gradient-to": "colors.pink.600",
          _dark: { "--tooltip-gradient-to": "colors.pink.500" },
          borderWidth: "2px",
          borderColor: "transparent",
          background:
            "linear-gradient(var(--tooltip-bg), var(--tooltip-bg)) padding-box, linear-gradient(90deg, var(--tooltip-gradient-from), var(--tooltip-gradient-to)) border-box",
        },
        // The arrow has to continue the gradient at whatever x it lands on, so
        // its rim is a fixed-position layer spanning the whole tooltip (fixed
        // resolves against the transformed positioner) that the arrow's
        // clip-path trims to a triangle, and its fill is a second triangle in
        // the container color that also opens the notch in the border.
        arrow: {
          "--arrow-size": "sizes.4",
          "--arrow-rim": "2.83px", // 2px rim measured perpendicular to a 45° edge
          "--arrow-base": "5.17px", // half-width of the fill triangle at the border
          _before: {
            content: '""',
            position: "fixed",
            inset: "-8px",
            background:
              "linear-gradient(90deg, var(--tooltip-gradient-from) 8px, var(--tooltip-gradient-to) calc(100% - 8px))",
          },
          _after: {
            content: '""',
            position: "absolute",
            bg: "var(--tooltip-bg)",
          },
          "[data-placement^=bottom] &": {
            clipPath: "polygon(50% 0, 100% 50%, 0 50%)",
            _after: {
              inset: "0 0 50% 0",
              clipPath:
                "polygon(50% var(--arrow-rim), calc(50% + var(--arrow-base)) 100%, calc(50% - var(--arrow-base)) 100%)",
            },
          },
          "[data-placement^=top] &": {
            clipPath: "polygon(50% 100%, 100% 50%, 0 50%)",
            _after: {
              inset: "50% 0 0 0",
              clipPath:
                "polygon(50% calc(100% - var(--arrow-rim)), calc(50% + var(--arrow-base)) 0, calc(50% - var(--arrow-base)) 0)",
            },
          },
          "[data-placement^=right] &": {
            clipPath: "polygon(0 50%, 50% 0, 50% 100%)",
            _after: {
              inset: "0 50% 0 0",
              clipPath:
                "polygon(var(--arrow-rim) 50%, 100% calc(50% - var(--arrow-base)), 100% calc(50% + var(--arrow-base)))",
            },
          },
          "[data-placement^=left] &": {
            clipPath: "polygon(100% 50%, 50% 0, 50% 100%)",
            _after: {
              inset: "0 0 0 50%",
              clipPath:
                "polygon(calc(100% - var(--arrow-rim)) 50%, 0 calc(50% - var(--arrow-base)), 0 calc(50% + var(--arrow-base)))",
            },
          },
        },
        arrowTip: { display: "none" },
      },
    },
  },

  defaultVariants: {
    size: "sm",
    variant: "subtle",
    colorPalette: "neutral",
  },
});
